// Main Section - Landing page for the Revolt application
// Based on the Revolt prototype design

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MainManager } from "./managers/main-manager";
import { MainLayout } from "../../components/Layout";
import { logger, useManager } from "../../core";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigationManager } from "../../managers/navigation-manager";
import { AppCreationErrorModal } from "../../components";
import {
  FloatingLogo,
  HeroTitle,
  SearchInput,
  AIProcessingStatus,
} from "./components";
import "./MainSection.css";

export function MainSection() {
  const { manager: mainManager, isInitialized } = useManager(MainManager);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [maxAttempts] = useState(3);
  const auth = useAuth();
  const location = useLocation();
  const navigationManager = useNavigationManager();
  const { user } = auth;
  const [restoredMessage, setRestoredMessage] = useState("");
  const [restoredTemplate, setRestoredTemplate] = useState("");
  const [aiPreviewContent, setAiPreviewContent] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("basic");
  const [templatesLoadedOnce, setTemplatesLoadedOnce] = useState(false);

  // Load template options on mount (same source as app create modal)
  useEffect(() => {
    const loadTemplates = async () => {
      if (templatesLoadedOnce && templateOptions.length > 0) return;
      try {
        const res = await fetch("/api/app-templates", { credentials: "include" });
        const data = await res.json();
        if (!res.ok || !Array.isArray(data)) {
          throw new Error(data?.error || "Failed to load templates");
        }
        let options = data.map((t, idx) => ({
          value: t.uniqueId || t.id || `template-${idx}`,
          label: t.displayName || t.uniqueId || "Template",
        }));
        options.sort((a, b) => {
          if (a.value === "basic") return -1;
          if (b.value === "basic") return 1;
          return 0;
        });
        const defaultOption = options.find((o) => o.value === "basic") || options[0];
        setTemplateOptions(options);
        setTemplatesLoadedOnce(true);
        if (!selectedTemplate || !options.some((o) => o.value === selectedTemplate)) {
          setSelectedTemplate(defaultOption ? defaultOption.value : "basic");
        }
      } catch (err) {
        logger.error({ tags: "main|template-error", color1: "red" }, "Template load error:", err);
        const fallback = [
          { value: "basic", label: "Basic" },
          { value: "p5", label: "P5" },
          { value: "website", label: "Website" },
        ];
        setTemplateOptions(fallback);
        setTemplatesLoadedOnce(true);
        setSelectedTemplate("basic");
      }
    };
    loadTemplates();
  }, [templatesLoadedOnce, templateOptions.length, selectedTemplate]);

  // Handle auth return payload restoration using new key-based system
  useEffect(() => {
    // Check for stored app idea message using new payload system
    const appIdeaPayload = navigationManager.consumePayload("appIdeaMessage");
    if (appIdeaPayload?.message) {
      setRestoredMessage(appIdeaPayload.message);
      if (appIdeaPayload.template) {
        setRestoredTemplate(appIdeaPayload.template);
      }
      logger.log(
        { tags: "main|auth-return", color1: "green" },
        "Restored app idea after login:",
        { message: appIdeaPayload.message, template: appIdeaPayload.template }
      );
      return;
    }

    // Fallback: Check for auth return data (legacy system for backward compatibility)
    const authReturnData = navigationManager.getAuthReturnData();
    const legacyPayload = authReturnData?.payload?.appIdeaMessage;
    if (legacyPayload) {
      setRestoredMessage(typeof legacyPayload === "string" ? legacyPayload : legacyPayload.message);
      if (typeof legacyPayload === "object" && legacyPayload.template) {
        setRestoredTemplate(legacyPayload.template);
      }
      navigationManager.clearAuthReturnData();
      logger.log(
        { tags: "main|auth-return|legacy", color1: "yellow" },
        "Restored app idea after login (legacy):",
        legacyPayload
      );
      return;
    }

    // Fallback: Check for location state (React Router state from navigation)
    const statePayload = location.state?.appIdeaMessage;
    if (statePayload) {
      setRestoredMessage(typeof statePayload === "string" ? statePayload : statePayload.message);
      if (typeof statePayload === "object" && statePayload.template) {
        setRestoredTemplate(statePayload.template);
      }
      logger.log(
        { tags: "main|location-state", color1: "green" },
        "Restored app idea from location state:",
        statePayload
      );
    }
  }, [auth.isAuthenticated, location.state, navigationManager]);

  // Apply restored template when options are loaded
  useEffect(() => {
    if (restoredTemplate && templateOptions.length > 0 && templateOptions.some((o) => o.value === restoredTemplate)) {
      setSelectedTemplate(restoredTemplate);
    }
  }, [restoredTemplate, templateOptions]);

  // Set up AI content update callback when manager is available
  useEffect(() => {
    if (mainManager && mainManager.setAIContentUpdateCallback) {
      mainManager.setAIContentUpdateCallback((content) => {
        setAiPreviewContent(content);
      });
    }

    // Cleanup: clear AI content when component unmounts
    return () => {
      setAiPreviewContent(null);
    };
  }, [mainManager]); // Only depend on mainManager, not isInitialized

  // Don't render until manager is initialized
  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--landing">
        <div className="loading-container">
          <div>Loading Revolt...</div>
        </div>
      </MainLayout>
    );
  }

  logger.log(
    {
      tags: "section|main|render",
      color1: "lime",
    },
    "MainSection rendering"
  );

  const handleLandingSubmit = async (message) => {
    if (!message || !message.trim()) return;

    // Check if user is authenticated first
    if (!auth.isAuthenticated) {
      // Store the message and template using new key-based payload system
      navigationManager.setPayload("appIdeaMessage", {
        message: message.trim(),
        template: selectedTemplate || "basic",
        timestamp: Date.now(),
        source: "mainSection",
      });

      logger.log(
        { tags: "main|auth-required", color1: "yellow" },
        "User not authenticated, stored app idea and redirecting to auth:",
        { message: message.trim(), template: selectedTemplate }
      );

      // Navigate to auth section - will return here after login
      await navigationManager.navigateTo("auth");
      return;
    }

    // User is authenticated, proceed with normal AI processing flow
    setIsProcessing(true);
    setCurrentAttempt(0);
    // Clear any previous AI content
    setAiPreviewContent(null);

    // Ensure AI callback is set (safety check in case useEffect hasn't run yet)
    if (
      mainManager &&
      mainManager.setAIContentUpdateCallback &&
      !mainManager.aiContentUpdateCallback
    ) {
      mainManager.setAIContentUpdateCallback((content) => {
        setAiPreviewContent(content);
      });
    }

    try {
      // Send message through the main manager (handles AI processing + app creation + navigation)
      await mainManager.sendMessage({
        userMessage: message.trim(),
        user: user,
        template: selectedTemplate || "basic",
      });
    } catch (error) {
      logger.error(
        {
          tags: "main|ai-processing|error",
          color1: "red",
        },
        "AI processing error:",
        error
      );

      // Parse error to extract structured error information
      // Check if error has structured data (from createAppAndNavigate)
      let errorCode = 'VALIDATION_ERROR';
      let errorMessage = error.message || 'Failed to create app';
      let details = {};

      // Try to parse structured error from error object
      if (error.errorCode) {
        errorCode = error.errorCode;
        errorMessage = error.error || error.message;
        details = error.details || {};
      } else if (error.message) {
        // Try to parse JSON from error message if it's a stringified error
        try {
          const parsed = JSON.parse(error.message);
          if (parsed.errorCode) {
            errorCode = parsed.errorCode;
            errorMessage = parsed.error || errorMessage;
            details = parsed.details || {};
          }
        } catch (e) {
          // Not JSON, use error message as-is
        }
      }

      // Set error data and show modal
      setErrorData({
        errorCode,
        errorMessage,
        details
      });
      setShowErrorModal(true);
    } finally {
      setIsProcessing(false);
      setCurrentAttempt(0);
      // Keep AI preview content visible so user can see final result
      // Only clear it when starting a new request
    }
  };


  return (
    <MainLayout contentClassName="main-content--landing">
      {/* Landing Container - Main Hero Section */}
      <div className="landing-container">
        <FloatingLogo />
        <HeroTitle />

        <SearchInput
          onSubmit={handleLandingSubmit}
          initialValue={restoredMessage}
          templateOptions={templateOptions}
          selectedTemplate={selectedTemplate}
          onTemplateChange={setSelectedTemplate}
        />

        <AIProcessingStatus
          isProcessing={isProcessing}
          currentAttempt={currentAttempt}
          maxAttempts={maxAttempts}
          aiContent={aiPreviewContent}
        />
      </div>

      <AppCreationErrorModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setErrorData(null);
        }}
        errorCode={errorData?.errorCode}
        errorMessage={errorData?.errorMessage}
        details={errorData?.details}
      />
    </MainLayout>
  );
}
